import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import pencilSVG from '@plone/volto/icons/pencil.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import checkSVG from '@plone/volto/icons/check.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const messages = defineMessages({
  empty: {
    id: 'assistantConversationsEmpty',
    defaultMessage: 'No previous conversations on this page yet.',
  },
  loadError: {
    id: 'assistantConversationsLoadError',
    defaultMessage: 'Conversation history could not be loaded.',
  },
  retry: {
    id: 'Retry',
    defaultMessage: 'Retry',
  },
  active: {
    id: 'assistantConversationActive',
    defaultMessage: 'Current conversation',
  },
  running: {
    id: 'assistantConversationRunning',
    defaultMessage: 'Running…',
  },
  actions: {
    id: 'assistantConversationActions',
    defaultMessage: 'Conversation actions',
  },
  rename: {
    id: 'Rename',
    defaultMessage: 'Rename',
  },
  delete: {
    id: 'Delete',
    defaultMessage: 'Delete',
  },
  save: {
    id: 'Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  deleteConfirm: {
    id: 'assistantConversationDeleteConfirm',
    defaultMessage: 'Delete this conversation?',
  },
});

const formatTime = (value, intl) => {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '';
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const relative = new Intl.RelativeTimeFormat(intl.locale, {
    numeric: 'auto',
  });
  if (Math.abs(seconds) < 60) return relative.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return relative.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relative.format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return relative.format(days, 'day');
  return intl.formatDate(timestamp, { month: 'short', day: 'numeric' });
};

const ConversationHistory = ({
  conversations,
  activeId,
  loading,
  error,
  busyId,
  onSelect,
  onRename,
  onDelete,
  onRetry,
}) => {
  const intl = useIntl();
  const [editingId, setEditingId] = React.useState(null);
  const [editingTitle, setEditingTitle] = React.useState('');
  const [deletingId, setDeletingId] = React.useState(null);
  const renameInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!editingId || !renameInputRef.current) return;
    renameInputRef.current.focus();
    renameInputRef.current.select();
  }, [editingId]);

  const startRename = (conversation) => {
    setDeletingId(null);
    setEditingId(conversation.id);
    setEditingTitle(conversation.title);
  };

  const submitRename = async (event) => {
    event.preventDefault();
    const title = editingTitle.replace(/\s+/g, ' ').trim();
    if (!title) return;
    if (await onRename(editingId, title)) setEditingId(null);
  };

  return (
    <div className="assistant-conversation-history">
      {loading ? (
        <div className="assistant-conversation-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      ) : conversations.length === 0 ? (
        <div className="assistant-conversation-empty">
          <p>
            {intl.formatMessage(error ? messages.loadError : messages.empty)}
          </p>
          {error && (
            <button type="button" onClick={onRetry}>
              {intl.formatMessage(messages.retry)}
            </button>
          )}
        </div>
      ) : (
        <>
          {error && (
            <div className="assistant-conversation-error" role="alert">
              <span>{intl.formatMessage(messages.loadError)}</span>
              <button type="button" onClick={onRetry}>
                {intl.formatMessage(messages.retry)}
              </button>
            </div>
          )}
          <ul className="assistant-conversation-list">
            {conversations.map((conversation) => {
              const active = conversation.id === activeId;
              const editing = editingId === conversation.id;
              const deleting = deletingId === conversation.id;
              const busy = busyId === conversation.id;

              return (
                <li
                  key={conversation.id}
                  className={`assistant-conversation-row${
                    active ? ' active' : ''
                  }`}
                >
                  {editing ? (
                    <form
                      className="assistant-conversation-rename"
                      onSubmit={submitRename}
                    >
                      <input
                        ref={renameInputRef}
                        value={editingTitle}
                        maxLength={120}
                        aria-label={intl.formatMessage(messages.rename)}
                        onChange={(event) =>
                          setEditingTitle(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <button
                        type="submit"
                        aria-label={intl.formatMessage(messages.save)}
                      >
                        <Icon name={checkSVG} size="14px" />
                      </button>
                      <button
                        type="button"
                        aria-label={intl.formatMessage(messages.cancel)}
                        onClick={() => setEditingId(null)}
                      >
                        <Icon name={clearSVG} size="14px" />
                      </button>
                    </form>
                  ) : deleting ? (
                    <div className="assistant-conversation-confirm">
                      <span>{intl.formatMessage(messages.deleteConfirm)}</span>
                      <button
                        type="button"
                        onClick={async () => {
                          if (await onDelete(conversation.id)) {
                            setDeletingId(null);
                          }
                        }}
                        disabled={busy}
                      >
                        {intl.formatMessage(messages.delete)}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setDeletingId(null)}
                      >
                        {intl.formatMessage(messages.cancel)}
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="assistant-conversation-main"
                        disabled={busy || conversation.running}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => onSelect(conversation)}
                      >
                        <span className="assistant-conversation-title-line">
                          <span className="assistant-conversation-title">
                            {conversation.title}
                          </span>
                          <span className="assistant-conversation-time">
                            {conversation.running
                              ? intl.formatMessage(messages.running)
                              : formatTime(conversation.updatedAt, intl)}
                          </span>
                        </span>
                        <span className="assistant-conversation-preview">
                          {conversation.preview}
                        </span>
                        {active && (
                          <span className="assistant-conversation-active-label">
                            {intl.formatMessage(messages.active)}
                          </span>
                        )}
                      </button>
                      <div
                        className="assistant-conversation-actions"
                        aria-label={intl.formatMessage(messages.actions)}
                      >
                        <button
                          type="button"
                          aria-label={intl.formatMessage(messages.rename)}
                          title={intl.formatMessage(messages.rename)}
                          disabled={busy}
                          onClick={() => startRename(conversation)}
                        >
                          <Icon name={pencilSVG} size="18px" />
                        </button>
                        <button
                          type="button"
                          className="danger"
                          aria-label={intl.formatMessage(messages.delete)}
                          title={intl.formatMessage(messages.delete)}
                          disabled={busy || conversation.running}
                          onClick={() => {
                            setEditingId(null);
                            setDeletingId(conversation.id);
                          }}
                        >
                          <Icon name={deleteSVG} size="18px" />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

export default ConversationHistory;
